import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../ui/select';
import { CalendarClock, FolderOpen } from 'lucide-react';
import { STATUS_META, STATUS_KEYS } from './rdTaskStatus';

export const StatusCell = ({ task, canEdit, onStatus }) => {
  const meta = STATUS_META[task.status] || STATUS_META.on_track;
  const Icon = meta.Icon;
  const label = <span className="inline-flex items-center gap-1"><Icon className="w-3 h-3" />{meta.label}</span>;
  if (!canEdit) {
    return <span className={`inline-flex text-[11px] px-2 py-1 rounded border ${meta.color}`} data-testid={`my-task-status-badge-${task.id}`}>{label}</span>;
  }
  return (
    <Select value={task.status} onValueChange={(v) => onStatus(task, v)}>
      <SelectTrigger className={`h-7 w-28 text-[11px] border ${meta.color}`} data-testid={`my-task-status-${task.id}`}>{label}</SelectTrigger>
      <SelectContent>
        {STATUS_KEYS.map(k => <SelectItem key={k} value={k} data-testid={`my-task-status-opt-${task.id}-${k}`}>{STATUS_META[k].label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

const DueCell = ({ task }) => {
  if (!task.due_date) return <span className="text-slate-400">No due date</span>;
  return (
    <span className={`inline-flex items-center gap-1 ${task.is_overdue ? 'text-rose-600 font-semibold' : 'text-slate-600'}`} data-testid={`my-task-due-${task.id}`}>
      <CalendarClock className="w-3.5 h-3.5" />
      {task.due_date}
      {task.is_overdue && <span className="ml-1 text-[10px] uppercase tracking-wide bg-rose-100 border border-rose-200 rounded px-1">Overdue</span>}
    </span>
  );
};

const Blockers = ({ task }) => {
  if (!task.blockers?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1" data-testid={`my-task-blockers-${task.id}`}>
      <span className="text-[10px] text-slate-500">Waiting on:</span>
      {task.blockers.map(b => (
        <Badge key={b.id} variant="outline" className={`text-[10px] border ${STATUS_META[b.status]?.color || ''}`} title={b.assignee_name ? `Owner: ${b.assignee_name}` : undefined}>
          {b.title.slice(0, 28)}{b.assignee_name ? ` · ${b.assignee_name}` : ''}
        </Badge>
      ))}
    </div>
  );
};

export default function MyTaskRow({ task, canEdit, onStatus }) {
  const navigate = useNavigate();
  return (
    <div
      className={`grid grid-cols-[auto_1fr] md:grid-cols-[8rem_1fr_14rem_12rem] gap-x-4 gap-y-1 px-4 py-3 items-start transition-colors hover:bg-slate-50 ${task.is_overdue ? 'border-l-4 border-l-rose-400' : 'border-l-4 border-l-transparent'}`}
      data-testid={`my-task-row-${task.id}`}
    >
      <StatusCell task={task} canEdit={canEdit} onStatus={onStatus} />
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-800" data-testid={`my-task-title-${task.id}`}>{task.title}</p>
        {task.description && <p className="text-xs text-slate-500 line-clamp-2">{task.description}</p>}
        {task.blocking_reason && <p className="text-xs text-amber-700 mt-0.5">Note: {task.blocking_reason}</p>}
        <Blockers task={task} />
      </div>
      <button
        onClick={() => navigate(`/rd/${task.project_id}?tab=tasks`)}
        className="col-start-2 md:col-start-auto text-left text-xs text-fuchsia-700 hover:text-fuchsia-900 hover:underline inline-flex items-center gap-1 min-w-0"
        data-testid={`my-task-project-link-${task.id}`}
      >
        <FolderOpen className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate">{task.project_name}</span>
        {task.project_status && <span className="text-slate-400 shrink-0">· {task.project_status}</span>}
      </button>
      <div className="col-start-2 md:col-start-auto text-xs"><DueCell task={task} /></div>
    </div>
  );
}
